import {
  CHAT_TYPE,
  IInfoType,
  IMessage,
  IOrganizationUser,
  IStarredMessageTypeData,
  ITemplate,
  MESSAGE_CONTENT_TYPE,
  TemplateKey,
  UnStarException,
} from "@home-health-notify/domain";
import {
  ApiErrorHandler,
  getRouteType,
  OrganizationContext,
  unSupportedIsMentionedUserHasPatient,
  unSupportedIsMentionedUserHasPhysician,
  useTranslation,
} from "hhn-commons";
import { useContext } from "react";
import { useNavigate } from "react-router";
import { ROUTES } from "../../routes";
import AlertListItemHeader from "../AlertListItemHeader";
import {
  MessageItemButton,
  MessageListItem,
  UnstarButton,
} from "./StarredMessage.style";
import MessagePanelItem from "../../message/messagePanelItem/MessagePanelItem";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";

interface StarredMessageListItemType {
  data: IStarredMessageTypeData;
  openMediaDialog: (message: IMessage) => void;
}

const StarredMessageListItem = ({
  data,
  openMediaDialog,
}: StarredMessageListItemType) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const tNsStarred = (key: string) => t(key, { ns: "starredMessages" });

  const navigate = useNavigate();
  const isPrivateChat =
    (data.message as IMessage).getMessage().chatType === CHAT_TYPE.PRIVATE;

  const goToChat = (data: IStarredMessageTypeData) => {
    const currentUserId = selectedOrgUser?.getOrganization().userProfile.id;
    let chatId = data.info?.id;
    if (isPrivateChat && chatId === currentUserId) {
      chatId = data.message?.getMessage().from;
    }

    navigate(`../${ROUTES.CHATS}/${getRouteType(data)}/${chatId}/message`, {
      state: { focusMessageId: data.message?.getMessage().id },
    });
  };

  const isSmartContent =
    data.message?.getMessage().contentType === MESSAGE_CONTENT_TYPE.SMART;

  const UnSupported = () => {
    throw new Error(
      "Panic: message selection not allowed on message item inside started alert tab",
    );
  };

  const getTemplateByKey = (
    templateKey: TemplateKey,
  ): ITemplate | undefined => {
    return selectedOrgUser?.getTemplateByKey(
      templateKey,
      data.info as IInfoType,
    );
  };

  const unstarMessage = async () => {
    try {
      await data.message?.removeStarredMessage();
    } catch (e) {
      if (e instanceof UnStarException) {
        enqueueSnackbar(tNs("message.unStarFailed"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  return (
    <MessageListItem alignItems="flex-start" divider disablePadding>
      <MessageItemButton
        onClick={() => goToChat(data)}
        data-testid="starred-message-list-item"
        disableGutters
      >
        <AlertListItemHeader data={data} />
      </MessageItemButton>
      <Box>
        {!!data.info && (
          <MessagePanelItem
            message={data.message as IMessage}
            selectedOrgUser={selectedOrgUser as IOrganizationUser}
            messageBubbleFullWidth={true}
            openMediaDialog={openMediaDialog}
            chatInfoAccess={{
              chatInfo: data.info,
              isMentionedUserHasPatient: unSupportedIsMentionedUserHasPatient,
              isMentionedUserHasPhysician:
                unSupportedIsMentionedUserHasPhysician,
              getTemplateByKey,
            }}
            isForcedFallbackContent={isSmartContent}
            hideContextMenu={true}
            selectMessage={UnSupported}
            hideAvatar
            isReplyPreview={true}
            footer={
              <UnstarButton disableRipple fullWidth onClick={unstarMessage}>
                ☆ {tNsStarred("tapToUnstar")}
              </UnstarButton>
            }
          />
        )}
      </Box>
    </MessageListItem>
  );
};

export default StarredMessageListItem;
